import React, { useState, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { Button, Grid, Loader, Modal, Table } from 'semantic-ui-react'
import { Time } from '../misc/Times'
import actions from '../../actions'
import { ccpProvider } from 'core/CcpProvider'
import Confirm from './Confirm'
import { HARD_DETACH_OUTCOME, hardDetachMessage, hardDetachOutcome, hardDetachToastOptions, isConversationsModalOpen, withoutConversation } from './agentListHelpers'

const ConversationsModal = ({ agent, onClose, onConversationRemoved }) => {
  const [conversations, setConversations] = useState([])
  const [salesCounts, setSalesCounts] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hardDetachConversation, setHardDetachConversation] = useState(null)
  const [hardDetaching, setHardDetaching] = useState(false)
  // Blocks a second click before the spinner state has re-rendered
  const hardDetachInFlight = useRef(false)

  useEffect(() => {
    loadConversations()
  }, [agent.id])

  // Sales counts are keyed by conversation, so they stay on the right row after a hard detach
  const loadSalesCounts = (loadedConversations) => {
    loadedConversations.forEach(async(conversation) => {
      try {
        const [response] = await ccpProvider.api.user.getUserSalesCount(
          { userUid: conversation.correspondentUuid },
          { identifier: conversation.identifier }
        )
        setSalesCounts((prev) => ({
          ...prev,
          [conversation.conversationUid]: response.sales || 0,
        }))
      } catch (err) {
        console.error(`Failed to load sales count for conversation ${conversation.conversationUid}`, err)
      }
    })
  }

  const loadConversations = async() => {
    setLoading(true)
    try {
      const response = await actions.agent.fetchAttachedConversations(agent.id)
      const loadedConversations = Object.values(response || {})
      setConversations(loadedConversations)
      loadSalesCounts(loadedConversations)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  const getUsersSalesCount = async(userUid, identifier) => {
    const [response] = await ccpProvider.api.user.getUserSalesCount({ userUid },{ identifier })

    return response.sales || 0
  }

  const hardDetach = async() => {
    if (hardDetachInFlight.current || !hardDetachConversation) {
      return
    }
    hardDetachInFlight.current = true
    setHardDetaching(true)

    let outcome = HARD_DETACH_OUTCOME.FAILED
    try {
      const [, err] = await actions.agent.removeConversation(agent.id, hardDetachConversation.conversationUid)
      outcome = hardDetachOutcome(err)
    } catch (err) {
      console.error(err)
    }

    const message = hardDetachMessage(outcome, agent.name)
    setHardDetachConversation(null)
    setHardDetaching(false)
    hardDetachInFlight.current = false

    if (outcome === HARD_DETACH_OUTCOME.FAILED) {
      toast.error(message, hardDetachToastOptions(outcome))
      return
    }

    if (outcome === HARD_DETACH_OUTCOME.SUCCESS) {
      toast.success(message, hardDetachToastOptions(outcome))
    } else {
      toast.info(message, hardDetachToastOptions(outcome))
    }
    const { conversationUid } = hardDetachConversation
    setConversations((current) => withoutConversation(current, conversationUid))
    onConversationRemoved?.(agent.id)
    if (withoutConversation(conversations, conversationUid).length === 0) {
      onClose()
    }
  }

  return (
    <>
      <Modal size="large" open={isConversationsModalOpen(conversations, error)}>
        <Modal.Header>Attached conversations to {agent?.name}</Modal.Header>
        <Modal.Content>
          <Modal.Description className="sale-modal-desctiption-section">
            {loading && <Loader active={loading} inverted/>}
            {error && (
              <Grid centered={true}>
                <Grid.Column style={{ display: 'flex', justifyContent: 'center'}}>
                  <h4>Could not load the conversations.</h4>
                </Grid.Column>
              </Grid>
            )}
            {conversations.length > 0 && <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Impersonator uuid</Table.HeaderCell>
                  <Table.HeaderCell>Correspondent uuid</Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Username</Table.HeaderCell>
                  <Table.HeaderCell>Site name</Table.HeaderCell>
                  <Table.HeaderCell>Billing cycle</Table.HeaderCell>
                  <Table.HeaderCell>actions</Table.HeaderCell>
                  <Table.HeaderCell>Hard detach</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {conversations.map((conversation) => {
                  return (
                    <Table.Row key={conversation.conversationUid}>
                      <Table.Cell>{conversation.impersonatorUuid}</Table.Cell>
                      <Table.Cell>{conversation.correspondentUuid}</Table.Cell>
                      <Table.Cell><Time time={conversation.date} format="MMM Do YY HH:mm"/>
                      </Table.Cell>
                      <Table.Cell>{conversation.username}</Table.Cell>
                      <Table.Cell>{conversation.siteName}</Table.Cell>
                      <Table.Cell>{salesCounts[conversation.conversationUid]}</Table.Cell>
                      <Table.Cell><Button color="green" as="a" target="_blank" href={`/admin/conversations/${conversation.conversationUid}`}>View</Button></Table.Cell>
                      <Table.Cell collapsing>
                        <Button
                          color="red"
                          title={`Remove this conversation from ${agent?.name} for good`}
                          onClick={() => setHardDetachConversation(conversation)}
                        >
                          Hard detach
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>}
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button color="red" onClick={onClose}>Close</Button>
        </Modal.Actions>
      </Modal>
      {hardDetachConversation && (
        <Confirm
          title="Hard detach conversation"
          yesLabel="Yes, hard detach"
          loading={hardDetaching}
          onCancel={() => setHardDetachConversation(null)}
          onSuccess={hardDetach}
        >
          <p>Hard detach this conversation from {agent?.name}?</p>
          <p>User: {hardDetachConversation.username} · Site: {hardDetachConversation.siteName}</p>
          <p>It is removed now and will never be attached to any agent or bot again. This can&apos;t be undone from the admin.</p>
        </Confirm>
      )}
    </>
  )
}

export default ConversationsModal
